import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, LuxuryButton } from "@/components/lux";
import api, { apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Wallet, RefreshCw, ShieldAlert, ExternalLink, ShieldCheck, KeyRound } from "lucide-react";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50";
const codeInp = inp + " tracking-[0.4em] text-center font-mono";

export default function AdminSecurity() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const { data: tfa, loading: tfaLoading, reload: reloadTfa } = useFetch("/admin/2fa/status");
  const { data: hw, loading: hwLoading, reload: reloadHw } = useFetch("/admin/withdrawal-hot-wallet");
  const enabled = !!tfa?.enabled;

  // password
  const [pf, setPf] = useState({ current_password: "", new_password: "", confirm: "", totp_code: "" });
  const [pBusy, setPBusy] = useState(false);
  const pUpd = (k) => (e) => setPf({ ...pf, [k]: e.target.value });
  const changePw = async (e) => {
    e.preventDefault();
    if (pf.new_password !== pf.confirm) return toast.error("Passwords do not match");
    if (pf.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (enabled && pf.totp_code.length !== 6) return toast.error("Enter your 6-digit 2FA code");
    setPBusy(true);
    try {
      await api.post("/user/change-password", { current_password: pf.current_password, new_password: pf.new_password, totp_code: pf.totp_code || undefined });
      toast.success("Password changed. Please sign in again.");
      await logout(); nav("/login");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPBusy(false); }
  };

  // 2FA setup
  const [setup, setSetup] = useState(null);
  const [enableCode, setEnableCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [tBusy, setTBusy] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const beginSetup = async () => {
    setTBusy(true);
    try { const { data } = await api.post("/admin/2fa/setup"); setSetup(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setTBusy(false); }
  };
  const doEnable = async () => {
    if (enableCode.length !== 6) return toast.error("Enter the 6-digit code");
    setTBusy(true);
    try {
      const { data } = await api.post("/admin/2fa/enable", { code: enableCode });
      setRecovery(data.recovery_codes); setSetup(null); setEnableCode(""); reloadTfa();
      toast.success("2FA enabled");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setTBusy(false); }
  };
  const doDisable = async () => {
    if (disableCode.length !== 6) return toast.error("Enter the 6-digit code");
    setTBusy(true);
    try { await api.post("/admin/2fa/disable", { code: disableCode }); setDisableCode(""); reloadTfa(); toast.success("2FA disabled"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setTBusy(false); }
  };

  // wallet rotate
  const [wf, setWf] = useState({ private_key: "", password: "", totp_code: "" });
  const [wBusy, setWBusy] = useState(false);
  const rotate = async (e) => {
    e.preventDefault();
    if (!wf.password) return toast.error("Enter your admin password");
    if (enabled && wf.totp_code.length !== 6) return toast.error("Enter your 6-digit 2FA code");
    setWBusy(true);
    try {
      const { data } = await api.post("/admin/withdrawal-wallet", wf);
      toast.success(`Hot wallet updated: ${data.address.slice(0, 10)}…`);
      setWf({ private_key: "", password: "", totp_code: "" }); reloadHw();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setWBusy(false); }
  };

  const lowBnb = hw && hw.bnb != null && hw.bnb < 0.0008;
  const noUsdt = hw && hw.usdt != null && hw.usdt <= 0;

  return (
    <div className="space-y-6">
      <PageTitle title="Security & Hot Wallet" subtitle="2FA, admin password and withdrawal wallet controls" />

      {/* 2FA */}
      <GlassCard className="p-6" data-testid="admin-2fa">
        <div className="mb-4 flex items-center gap-2"><ShieldCheck size={18} className="text-[var(--gold)]" /><h3 className="font-semibold text-white">Two-Factor Authentication (2FA)</h3>
          <span className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-bold ${enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`} data-testid="2fa-status">{tfaLoading ? "…" : enabled ? "ENABLED" : "DISABLED"}</span>
        </div>
        {recovery ? (
          <div data-testid="2fa-recovery">
            <p className="text-sm text-emerald-300">2FA enabled! Save these recovery codes (each works once):</p>
            <pre className="mt-2 grid grid-cols-2 gap-1 rounded-xl bg-black/40 p-3 font-mono text-xs text-white sm:grid-cols-4">{recovery.join("  ")}</pre>
            <LuxuryButton variant="dark" className="mt-3" onClick={() => { navigator.clipboard.writeText(recovery.join("\n")); toast.success("Copied"); }}>Copy codes</LuxuryButton>
          </div>
        ) : !enabled ? (
          !setup ? (
            <div>
              <p className="mb-3 text-sm text-[var(--text-dim)]">Protect password changes and hot-wallet edits. Use Google Authenticator or Authy.</p>
              <LuxuryButton onClick={beginSetup} disabled={tBusy} data-testid="2fa-setup-btn">Set up 2FA</LuxuryButton>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-sm text-white">1. Scan this QR in your authenticator app:</p>
                <img src={setup.qr_data_uri} alt="2FA QR" className="h-44 w-44 rounded-lg bg-white p-2" data-testid="2fa-qr" />
                <p className="mt-2 break-all text-[11px] text-[var(--text-dim)]">Manual key: <span className="select-all font-mono text-white">{setup.secret}</span></p>
              </div>
              <div>
                <p className="mb-2 text-sm text-white">2. Enter the current 6-digit code:</p>
                <input className={codeInp} inputMode="numeric" maxLength={6} value={enableCode} onChange={(e) => setEnableCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" data-testid="2fa-enable-code" />
                <LuxuryButton className="mt-3" onClick={doEnable} disabled={tBusy || enableCode.length !== 6} data-testid="2fa-enable-btn">Enable 2FA</LuxuryButton>
              </div>
            </div>
          )
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <p className="mb-1 text-xs text-[var(--text-dim)]">Disable 2FA (enter current code)</p>
              <input className={codeInp + " w-40"} inputMode="numeric" maxLength={6} value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" data-testid="2fa-disable-code" />
            </div>
            <LuxuryButton variant="maroon" onClick={doDisable} disabled={tBusy || disableCode.length !== 6} data-testid="2fa-disable-btn">Disable</LuxuryButton>
          </div>
        )}
      </GlassCard>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Password */}
        <GlassCard className="p-6" data-testid="admin-change-password">
          <div className="mb-4 flex items-center gap-2"><Lock size={18} className="text-[var(--gold)]" /><h3 className="font-semibold text-white">Change Admin Password</h3></div>
          <form onSubmit={changePw} className="space-y-3">
            <input className={inp} type="password" placeholder="Current password" value={pf.current_password} onChange={pUpd("current_password")} data-testid="admin-sec-current" required />
            <input className={inp} type="password" placeholder="New password" value={pf.new_password} onChange={pUpd("new_password")} data-testid="admin-sec-new" required />
            <input className={inp} type="password" placeholder="Confirm new password" value={pf.confirm} onChange={pUpd("confirm")} data-testid="admin-sec-confirm" required />
            {enabled && <input className={codeInp} inputMode="numeric" maxLength={6} value={pf.totp_code} onChange={(e) => setPf({ ...pf, totp_code: e.target.value.replace(/\D/g, "") })} placeholder="2FA code" data-testid="admin-sec-totp" />}
            <LuxuryButton type="submit" disabled={pBusy} data-testid="admin-sec-submit">Update Password</LuxuryButton>
          </form>
          <p className="mt-3 text-xs text-[var(--text-dim)]">Forgot it entirely? Use "Forgot password" on the login page — a reset link goes to the admin email.</p>
        </GlassCard>

        {/* Hot wallet */}
        <GlassCard className="p-6" data-testid="admin-hot-wallet">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2"><Wallet size={18} className="text-[var(--gold)]" /><h3 className="font-semibold text-white">Withdrawal Hot Wallet</h3></div>
            <button onClick={reloadHw} className="grid h-8 w-8 place-items-center rounded-lg glass-2 text-white" title="Refresh" data-testid="hw-refresh"><RefreshCw size={14} /></button>
          </div>
          {hwLoading ? <div className="skeleton h-28" /> : hw ? (
            <div className="space-y-3">
              <div className="rounded-xl glass-2 p-3">
                <p className="text-xs text-[var(--text-dim)]">Mode</p>
                <p className={`text-sm font-bold ${hw.enabled ? "text-emerald-400" : "text-amber-300"}`} data-testid="hw-mode">{hw.mode}</p>
              </div>
              <div className="rounded-xl glass-2 p-3">
                <p className="text-xs text-[var(--text-dim)]">Sending Address (public)</p>
                {hw.address ? (
                  <a href={`https://bscscan.com/address/${hw.address}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 break-all text-sm text-[var(--gold)] hover:underline" data-testid="hw-address">{hw.address} <ExternalLink size={12} /></a>
                ) : <p className="text-sm text-[var(--text-dim)]">Not configured</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl glass-2 p-3"><p className="text-xs text-[var(--text-dim)]">USDT Balance</p><p className={`text-lg font-semibold ${noUsdt ? "text-rose-400" : "text-white"}`} data-testid="hw-usdt">{hw.usdt != null ? `$${money(hw.usdt)}` : "—"}</p></div>
                <div className="rounded-xl glass-2 p-3"><p className="text-xs text-[var(--text-dim)]">BNB (gas)</p><p className={`text-lg font-semibold ${lowBnb ? "text-rose-400" : "text-white"}`} data-testid="hw-bnb">{hw.bnb != null ? hw.bnb.toFixed(5) : "—"}</p></div>
              </div>
              {(lowBnb || noUsdt) && (
                <div className="flex items-start gap-2 rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-300" data-testid="hw-low-warning">
                  <ShieldAlert size={16} className="mt-0.5 shrink-0" /><span>{noUsdt ? "USDT balance is empty — payouts will fail. " : ""}{lowBnb ? "BNB gas is low — top up BNB." : ""}</span>
                </div>
              )}
              {/* Rotate in-app (no redeploy) */}
              <div className="rounded-xl border border-white/10 bg-white/5 p-3" data-testid="hw-rotate">
                <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-white"><KeyRound size={15} className="text-[var(--gold)]" /> Change hot wallet (no redeploy)</p>
                <form onSubmit={rotate} className="space-y-2">
                  <input className={inp} type="password" placeholder="New wallet private key" value={wf.private_key} onChange={(e) => setWf({ ...wf, private_key: e.target.value })} data-testid="hw-new-key" required autoComplete="off" />
                  <input className={inp} type="password" placeholder="Your admin password" value={wf.password} onChange={(e) => setWf({ ...wf, password: e.target.value })} data-testid="hw-rotate-pw" required autoComplete="off" />
                  {enabled && <input className={codeInp} inputMode="numeric" maxLength={6} value={wf.totp_code} onChange={(e) => setWf({ ...wf, totp_code: e.target.value.replace(/\D/g, "") })} placeholder="2FA code" data-testid="hw-rotate-totp" required />}
                  <LuxuryButton type="submit" variant="gold" disabled={wBusy} data-testid="hw-rotate-btn">Save New Wallet</LuxuryButton>
                  <p className="text-[11px] text-[var(--text-dim)]">Confirmed with your password{enabled ? " + 2FA" : ""}. The key is encrypted at rest and never shown again. Fund the new wallet with USDT + BNB.</p>
                </form>
              </div>
            </div>
          ) : <p className="text-sm text-[var(--text-dim)]">Unable to load hot wallet status.</p>}
        </GlassCard>
      </div>
    </div>
  );
}
